import React, { useState, useEffect } from "react";
import { TextField, Button, Grid } from "@mui/material";
import { PMDialog } from "../../../utils/PMDialog";
import { useTranslation } from "react-i18next";
import { contractLinesApi } from "../../../../api/contract_lines";
import { axiosWithToast } from "../../../Utils";
import ItemSelectorV2 from "../../../utils/Selector/ItemSelectorV2";
import { Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { fetchUnits } from "../../../../api/unit";

export const ContractLineDialog = ({
  open,
  onClose,
  onSuccess,
  project,
  contractLine,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    contract_quantity: "",
    unit_price: "",
    unit_id: null,
    product_id: null,
    product: null,
  });
  const [productSelectorOpen, setProductSelectorOpen] = useState(false);
  const [units, setUnits] = useState([]);

  useEffect(() => {
    if (open) {
      if (contractLine) {
        setFormData({
          title: contractLine.title || "",
          description: contractLine.description || "",
          contract_quantity: contractLine.contract_quantity || "",
          unit_price: contractLine.unit_price || "",
          unit_id: contractLine.unit_id || null,
          product_id: contractLine.product_id || null,
          product: contractLine.product || null,
        });
      } else {
        setFormData({
          title: "",
          description: "",
          contract_quantity: "",
          unit_price: "",
          unit_id: null,
          product_id: null,
          product: null,
        });
      }
      fetchUnitsData();
    }
  }, [open, contractLine]);

  const fetchUnitsData = async () => {
    try {
      const response = await fetchUnits({});
      if (response.success && response.data) {
        setUnits(response.data);
      }
    } catch (error) {
      console.error("Error fetching units:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!project?.id) return;

    setLoading(true);

    const payload = {
      title: formData.title,
      description: formData.description,
      contract_quantity: parseFloat(formData.contract_quantity) || 0,
      unit_price: parseFloat(formData.unit_price) || 0,
      unit_id: formData.unit_id,
      product_id: formData.product_id,
      group_row: false,
    };

    try {
      if (contractLine) {
        await axiosWithToast(
          contractLinesApi.updateContractLine(project.id, contractLine.id, payload),
          () => {
            onSuccess();
          },
          () => {},
          () => {},
          "Oppdaterer kontraktslinje..."
        );
      } else {
        await axiosWithToast(
          contractLinesApi.createContractLine(project.id, payload),
          () => {
            onSuccess();
          },
          () => {},
          () => {},
          "Oppretter kontraktslinje..."
        );
      }
    } catch (error) {
      console.error("Error saving contract line:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleProductSelect = (products) => {
    if (products && products.length > 0) {
      const product = products[0];
      setFormData({
        ...formData,
        product_id: product.id,
        product: product,
        unit_id: product.unit?.id || formData.unit_id,
        unit_price: product.sales_price || formData.unit_price,
        title: product.displayTitle || product.title || formData.title,
      });
    }
    setProductSelectorOpen(false);
  };

  const formatPriceInput = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    const stringValue = String(value);
    const cleaned = stringValue.replace(/[^0-9,.]/g, "");
    return cleaned.replace(/\./g, ",");
  };

  const formatPriceWithTwoDecimals = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    const stringValue = String(value).trim();
    if (stringValue === "") return "";
    
    if (stringValue.includes(",")) {
      const parts = stringValue.split(",");
      const integerPart = parts[0] || "";
      const decimalPart = parts[1] || "";
      
      let formattedDecimal = decimalPart.substring(0, 2);
      if (formattedDecimal.length === 1) {
        formattedDecimal = formattedDecimal + "0";
      } else if (formattedDecimal.length === 0) {
        formattedDecimal = "00";
      }
      
      return `${integerPart},${formattedDecimal}`;
    }
    
    if (/^\d+$/.test(stringValue)) {
      return `${stringValue},00`;
    }
    
    return stringValue;
  };

  const handlePriceBlur = (e) => {
    const value = e.target.value;
    if (value && value.trim() !== "") {
      const formatted = formatPriceWithTwoDecimals(value);
      setFormData({
        ...formData,
        unit_price: formatted,
      });
    }
  };

  const calculateTotal = () => {
    const quantity = parseFloat(formData.contract_quantity) || 0;
    const price = parseFloat(String(formData.unit_price).replace(/,/g, ".")) || 0;
    return quantity * price;
  };

  const formatter = new Intl.NumberFormat("nb-NO", {
    style: "currency",
    currency: "NOK",
  });

  return (
    <>
      <PMDialog
        open={open}
        onClose={onClose}
        title={contractLine ? "Rediger kontraktslinje" : "Ny kontraktslinje"}
        size="2xl"
        closeOnOutsideClick={false}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Tittel */}
          <TextField
            label="Tittel"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            fullWidth
            variant="standard"
          />

          {/* Beskrivelse */}
          <TextField
            label="Beskrivelse"
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            fullWidth
            multiline
            rows={3}
            variant="standard"
          />

          {/* Produkt */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Produkt
            </label>
            <button
              type="button"
              onClick={() => setProductSelectorOpen(true)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-left bg-white hover:bg-gray-50"
            >
              {formData.product ? (
                <div className="font-medium text-blue-700">
                  {formData.product.displayTitle || formData.product.title || "Produkt valgt"}
                </div>
              ) : (
                <div className="text-gray-500">Klikk for å velge produkt</div>
              )}
            </button>
          </div>

          {/* Kontraktsmengde og Enhet */}
          <Grid container spacing={2}>
            <Grid item xs={8}>
              <TextField
                label="Kontraktsmengde"
                type="number"
                value={formData.contract_quantity}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    contract_quantity: e.target.value,
                  })
                }
                fullWidth
                variant="standard"
                inputProps={{ step: "0.01", min: "0" }}
              />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth variant="standard">
                <InputLabel>Enhet</InputLabel>
                <Select
                  value={formData.unit_id || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, unit_id: e.target.value || null })
                  }
                  label="Enhet"
                >
                  <MenuItem value="">
                    <em>Ingen valgt</em>
                  </MenuItem>
                  {units.map((unit) => (
                    <MenuItem key={unit.id} value={unit.id}>
                      {unit.displayTitle || unit.title || `Enhet ${unit.id}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Enhetspris */}
          <TextField
            label="Enhetspris (ekskl. mva)"
            value={formData.unit_price ? formatPriceInput(formData.unit_price) : ""}
            onChange={(e) => {
              const formatted = formatPriceInput(e.target.value);
              setFormData({ ...formData, unit_price: formatted });
            }}
            onBlur={handlePriceBlur}
            fullWidth
            variant="standard"
            InputProps={{
              endAdornment: <span className="ml-2">kr</span>,
            }}
          />

          {/* Read-only: Utført mengde */}
          {contractLine && (
            <TextField
              label="Utført mengde"
              value={`${contractLine.completed_quantity || 0} ${contractLine.unit?.title || ""}`}
              disabled
              fullWidth
              variant="standard"
              helperText="Utført mengde registreres via 'Registrer fremdrift'"
            />
          )}

          {/* Read-only: Totalpris */}
          <TextField
            label="Totalpris (kontrakt)"
            value={formatter.format(calculateTotal())}
            disabled
            fullWidth
            variant="standard"
          />

          {/* Actions */}
          <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
            <Button onClick={onClose} disabled={loading}>
              Avbryt
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {loading ? "Lagrer..." : contractLine ? "Oppdater" : "Opprett"}
            </Button>
          </div>
        </form>
      </PMDialog>

      {/* Product Selector */}
      <ItemSelectorV2
        multiple={false}
        open={productSelectorOpen}
        onChange={handleProductSelect}
        module={"product"}
        selectedItems={formData.product ? [formData.product] : []}
        autoClose={true}
        compact={true}
        onClose={() => setProductSelectorOpen(false)}
      />
    </>
  );
};

